<template>
  <div>
    <b-navbar
      toggleable="lg"
      type="dark"
      variant="primary"
      fixed="top"
      class="ga-navbar"
      v-on-clickaway="hideNavBar"
    >
      <b-container fluid class="ga-navbar-container content">
        <div class="ga-layout-row ga-layout-row--navbar d-none d-lg-grid">
          <div class="ga-layout-gutter--left">
            <b-navbar-brand href="/" class="ga-navbar-brand">
              <img
                :src="logoUrl"
                alt="gender app"
                class="ga-navbar-brand__logo"
                @error="onLogoError"
              />
            </b-navbar-brand>
          </div>
          <div class="ga-layout-main">
            <div class="ga-navbar-search-shell">
              <DictSearchInput />
              <DictSortControl v-if="showNavbarSort" />
            </div>
          </div>
          <div class="ga-layout-gutter--right">
            <b-navbar-nav class="ga-navbar-actions ga-navbar-actions--side">
              <b-nav-item
                :to="{ name: 'add-in' }"
                class="ga-nav-primary"
                v-b-tooltip.hover.bottom="addInTooltipConfig"
              >
                <i class="fas fa-plug ga-nav-item-icon" aria-hidden="true"></i>{{ $t('Add-In') }}
              </b-nav-item>
              <b-nav-item :to="{ name: 'dictionary.helfen' }" class="btn-nav-helfen">
                <span class="ga-nav-helfen-btn">
                  <span
                    v-if="isLoggedIn()"
                    class="nav-karma-wrap"
                    v-b-tooltip.hover.bottom="karmaTooltipConfig"
                    @click.stop
                  >
                    <span
                      class="nav-karma-in-btn__score"
                      :class="{ 'nav-karma-in-btn__score--loading': !karmaLoaded }"
                    >{{ karmaDisplay }}</span>
                    <span class="nav-karma-in-btn__dot" aria-hidden="true">·</span>
                  </span>
                  <span class="ga-nav-helfen-btn__label">{{ $t('helfen.nav') }}</span>
                </span>
              </b-nav-item>
              <b-nav-item-dropdown class="nav-overflow" right>
                <template v-slot:button-content>
                  <span class="ga-navbar-menu-burger" aria-hidden="true">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                  <span class="sr-only">{{ $t('more') }}</span>
                </template>
                <b-dropdown-header v-if="isLoggedIn()">
                  {{ getTruncateUsername(getUsername(), 20) }}
                </b-dropdown-header>
                <b-dropdown-item v-if="isLoggedIn()" :to="{ name: 'auth.profile', params: { username: getUsername() } }">
                  {{ $t('Profile') }}
                </b-dropdown-item>
                <b-dropdown-item v-if="isLoggedIn()" :to="{ name: 'dictionary.entry.add' }">{{ $t('Add Entry') }}</b-dropdown-item>
                <b-dropdown-item v-else href="#" @click="showRegistrationModal">{{ $t('Add Entry') }}</b-dropdown-item>
                <b-dropdown-item v-show="isLoggedIn() && isTeamAdmin()" :to="{ name: 'team.team-dashboard' }">
                  {{ $t('team-dashboard') }}
                </b-dropdown-item>
                <b-dropdown-item v-if="isLoggedIn()" href="#" @click="makeLogout">{{ $t('Sign Out') }}</b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item :to="{ name: 'lektorat.website-check' }">{{ $t('Website Check') }}</b-dropdown-item>
                <b-dropdown-item :to="{ name: 'about' }">{{ $t('About gender app') }}</b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item :to="{ name: 'language' }">{{ $t('Language') }}</b-dropdown-item>
                <b-dropdown-item :to="{ name: 'support' }">{{ $t('Support') }}</b-dropdown-item>
                <b-dropdown-item :to="{ name: 'team' }">{{ $t('Team') }}</b-dropdown-item>
                <b-dropdown-item :to="{ name: 'manual' }">{{ $t('Manual') }}</b-dropdown-item>
                <b-dropdown-item :to="{ name: 'commercial' }">{{ $t('Commercial Use') }}</b-dropdown-item>
                <b-dropdown-item :to="{ name: 'inprint' }">{{ $t('Inprint') }}</b-dropdown-item>
                <b-dropdown-item :to="{ name: 'lektorat.solution' }">{{ $t('Lektorat') }}</b-dropdown-item>
                <template v-if="!isLoggedIn()">
                  <b-dropdown-divider></b-dropdown-divider>
                  <b-dropdown-item :to="{ name: 'auth.login' }">{{ $t('Login') }}</b-dropdown-item>
                  <b-dropdown-item :to="{ name: 'auth.register' }">{{ $t('Register') }}</b-dropdown-item>
                </template>
              </b-nav-item-dropdown>
            </b-navbar-nav>
          </div>
        </div>

        <div class="ga-navbar-mobile d-lg-none">
          <div class="ga-navbar-mobile__bar">
            <b-navbar-brand href="/" class="ga-navbar-brand ga-navbar-brand--mobile">
              <img
                :src="logoMarkUrl"
                alt="gender app"
                class="ga-navbar-brand__mark"
                @error="onLogoMarkError"
              />
            </b-navbar-brand>

            <div class="ga-navbar-mobile__search">
              <div class="ga-navbar-search-shell">
                <DictSearchInput />
                <DictSortControl v-if="showNavbarSort" />
              </div>
            </div>

            <b-navbar-toggle target="nav-collapse" class="ga-navbar-toggle ga-navbar-mobile__toggle">
              <span class="ga-navbar-menu-burger" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </b-navbar-toggle>
          </div>

          <b-collapse
            id="nav-collapse"
            is-nav
            v-model="isNavbarCollapseOpen"
            class="ga-navbar-collapse"
          >
            <b-navbar-nav class="ga-navbar-right">
              <template v-if="isLoggedIn()">
                <b-nav-text class="ga-navbar-mobile__user">{{ getTruncateUsername(getUsername(), 20) }}</b-nav-text>
                <b-nav-item :to="{ name: 'auth.profile', params: { username: getUsername() } }" @click="hideNavBar">
                  {{ $t('Profile') }}
                </b-nav-item>
                <b-nav-item :to="{ name: 'dictionary.entry.add' }" @click="hideNavBar">{{ $t('Add Entry') }}</b-nav-item>
                <b-nav-item v-show="isTeamAdmin()" :to="{ name: 'team.team-dashboard' }" @click="hideNavBar">
                  {{ $t('team-dashboard') }}
                </b-nav-item>
                <b-nav-item href="#" @click="makeLogout">{{ $t('Sign Out') }}</b-nav-item>
                <li class="nav-item ga-nav-mobile-divider" aria-hidden="true"></li>
              </template>
              <b-nav-item
                :to="{ name: 'add-in' }"
                class="ga-nav-primary"
                v-b-tooltip.hover.bottom="addInTooltipConfig"
                @click="hideNavBar"
              >
                <i class="fas fa-plug ga-nav-item-icon" aria-hidden="true"></i>{{ $t('Add-In') }}
              </b-nav-item>
              <b-nav-item :to="{ name: 'dictionary.helfen' }" class="btn-nav-helfen" @click="hideNavBar">
                <span class="ga-nav-helfen-btn">
                  <span
                    v-if="isLoggedIn()"
                    class="nav-karma-wrap"
                    v-b-tooltip.hover.bottom="karmaTooltipConfig"
                    @click.stop
                  >
                    <span
                      class="nav-karma-in-btn__score"
                      :class="{ 'nav-karma-in-btn__score--loading': !karmaLoaded }"
                    >{{ karmaDisplay }}</span>
                    <span class="nav-karma-in-btn__dot" aria-hidden="true">·</span>
                  </span>
                  <span class="ga-nav-helfen-btn__label">{{ $t('helfen.nav') }}</span>
                </span>
              </b-nav-item>
              <li class="nav-item ga-nav-mobile-divider" aria-hidden="true"></li>
              <b-nav-item :to="{ name: 'lektorat.website-check' }" @click="hideNavBar">{{ $t('Website Check') }}</b-nav-item>
              <b-nav-item :to="{ name: 'about' }" @click="hideNavBar">{{ $t('About gender app') }}</b-nav-item>
              <b-nav-item :to="{ name: 'language' }" @click="hideNavBar">{{ $t('Language') }}</b-nav-item>
              <b-nav-item :to="{ name: 'support' }" @click="hideNavBar">{{ $t('Support') }}</b-nav-item>
              <b-nav-item :to="{ name: 'team' }" @click="hideNavBar">{{ $t('Team') }}</b-nav-item>
              <b-nav-item :to="{ name: 'manual' }" @click="hideNavBar">{{ $t('Manual') }}</b-nav-item>
              <b-nav-item :to="{ name: 'commercial' }" @click="hideNavBar">{{ $t('Commercial Use') }}</b-nav-item>
              <b-nav-item :to="{ name: 'inprint' }" @click="hideNavBar">{{ $t('Inprint') }}</b-nav-item>
              <b-nav-item :to="{ name: 'lektorat.solution' }" @click="hideNavBar">{{ $t('Lektorat') }}</b-nav-item>
              <template v-if="!isLoggedIn()">
                <li class="nav-item ga-nav-mobile-divider" aria-hidden="true"></li>
                <b-nav-item :to="{ name: 'auth.login' }" @click="hideNavBar">{{ $t('Login') }}</b-nav-item>
                <b-nav-item :to="{ name: 'auth.register' }" @click="hideNavBar">{{ $t('Register') }}</b-nav-item>
              </template>
            </b-navbar-nav>
          </b-collapse>
        </div>
      </b-container>
    </b-navbar>

    <div class="ga-navbar-spacer" aria-hidden="true"></div>

    <LoginWallModal ref="registerModal" />
  </div>
</template>

<script>
import { AuthService } from '../services/auth.service'
import { ProfileDictionaryEntryService } from '../services/profileDictionaryEntryService'
import LoginWallModal from './LoginWallModal'
import DictSearchInput from './DictSearchInput'
import DictSortControl from './DictSortControl'
import { installNavbarLayoutSync, syncNavbarLayoutColumns, syncNavbarHeight } from '@/utils/layoutSync'

var Vue = require('vue')
export default {
  name: 'TopNavBar',
  components: { LoginWallModal, DictSearchInput, DictSortControl },
  data () {
    return {
      logoUrl: '/img/logo.png',
      logoMarkUrl: '/img/icons/favicon-96x96.png',
      logoFallbackUsed: false,
      logoMarkFallbackUsed: false,
      total_likes: -1,
      total_dislikes: -1,
      error: '',
      isNavbarCollapseOpen: false
    }
  },
  props: ['secondTierLoadingDone'],
  watch: {
    karmaLoaded () {
      this.$nextTick(() => this.scheduleLayoutSync())
    },
    isNavbarCollapseOpen () {
      this.$nextTick(() => this.scheduleLayoutSync())
    }
  },
  computed: {
    karmaTooltipConfig () {
      return {
        title: this.$t('karma.tooltip'),
        container: 'body',
        boundary: 'window'
      }
    },
    addInTooltipConfig () {
      return {
        title: this.$t('Add-In.tooltip'),
        container: 'body',
        boundary: 'window'
      }
    },
    karmaLoaded () {
      return this.total_likes > -1
    },
    karmaScore () {
      return this.total_likes - this.total_dislikes
    },
    karmaDisplay () {
      return this.karmaLoaded ? String(this.karmaScore) : '0'
    },
    showNavbarSort () {
      const name = this.$route.name
      return name === 'home' || name === 'specific' || name === 'homepage' || name === 'auth.profile'
    }
  },
  methods: {
    scheduleLayoutSync () {
      syncNavbarLayoutColumns(this.$el)
      syncNavbarHeight(this.$el)
    },
    hideNavBar () {
      this.isNavbarCollapseOpen = false
    },
    onLogoError () {
      if (!this.logoFallbackUsed) {
        this.logoFallbackUsed = true
        this.logoUrl = 'https://genderapp.org/img/logo.png'
      }
    },
    onLogoMarkError () {
      if (!this.logoMarkFallbackUsed) {
        this.logoMarkFallbackUsed = true
        this.logoMarkUrl = 'https://genderapp.org/img/icons/favicon-96x96.png'
      }
    },
    makeLogout () {
      AuthService.makeLogout()
      window.location.reload()
    },
    getUsername () {
      return Vue.default.cookie.get('username', 'Profil')
    },
    isLoggedIn () {
      const token = Vue.default.cookie.get('token')
      const username = Vue.default.cookie.get('username')
      return !!token && !!username
    },
    isTeamAdmin () {
      const isAdminUser = Vue.default.cookie.get('is_team_admin')
      const isAdminUserLowercase = isAdminUser ? isAdminUser.toLowerCase() : 'false'
      return isAdminUserLowercase === 'true'
    },
    showRegistrationModal () {
      this.$refs.registerModal.show()
    },
    hideRegistrationModal () {
      this.$refs.registerModal.hide()
    },
    getTruncateUsername (username, length, clamp) {
      clamp = clamp || '...'
      var node = document.createElement('div')
      node.innerHTML = username
      var content = node.textContent
      return content.length > length ? content.slice(0, length) + clamp : content
    },
    async getUserTermStatistics () {
      await this.siteLoaded()
      await ProfileDictionaryEntryService.getProfileDictionaryEntryStatistics(this.getUsername())
        .then((response) => {
          this.total_likes = response.data.total_likes
          this.total_dislikes = response.data.total_dislikes
        })
        .catch((error) => {
          console.log(error)
          this.total_likes = 0
          this.total_dislikes = 0
        })
    },

    siteLoaded: async function () {
      while (!this.secondTierLoadingDone) {
        await wait(100)
      }
      return Promise.resolve('ACK')
    }
  },

  mounted () {
    this.getUserTermStatistics()
    this.$nextTick(() => {
      this._layoutSyncTeardown = installNavbarLayoutSync(this.$el)
    })
  },

  beforeDestroy () {
    if (this._layoutSyncTeardown) {
      this._layoutSyncTeardown()
    }
  }
}

async function wait (ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms)
  })
}
</script>

<style scoped>
</style>
